"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type CurrentUser = "suhyeon" | "wife";

type Ctx = {
  user: CurrentUser | null;
  setUser: (u: CurrentUser) => void;
  ready: boolean;
};

const UserCtx = createContext<Ctx>({ user: null, setUser: () => {}, ready: false });

const KEY = "budget:currentUser";

export function useCurrentUser() {
  return useContext(UserCtx);
}

function normalize(v: string | null | undefined): CurrentUser | null {
  return v === "suhyeon" || v === "wife" ? v : null;
}

/**
 * SSR이 쿠키에서 풀어준 initialOwner를 1차 시드로 사용.
 * - 첫 렌더부터 owner=initialOwner → 카드 깜빡임 제거
 * - localStorage 값이 다르면 SSR 값(=실제 로그인 세션)을 우선해서 덮어씀
 *   (기기 공용 환경에서 다른 계정 로그인 시 stale localStorage 가 잘못된 카드를 띄우던 버그 차단)
 */
export function UserProvider({
  children,
  initialOwner,
}: {
  children: React.ReactNode;
  initialOwner?: CurrentUser | null;
}) {
  const seed = normalize(initialOwner);
  // SSR/CSR 첫 렌더에서 동일한 값이 나와야 hydration mismatch 가 안 난다.
  // → 첫 렌더에서 localStorage 를 읽지 않고 seed 만 사용.
  const [user, setUserState] = useState<CurrentUser | null>(seed);
  const [ready, setReady] = useState<boolean>(seed !== null);

  useEffect(() => {
    // SSR이 owner를 풀어줬으면 그것이 진실 — localStorage 도 동기화하고 끝.
    if (seed) {
      try {
        if (localStorage.getItem(KEY) !== seed) localStorage.setItem(KEY, seed);
      } catch {}
      setReady(true);
      return;
    }
    // 쿠키 미해소(예: 비로그인) 일 때만 localStorage 폴백 사용.
    const saved = typeof window !== "undefined" ? localStorage.getItem(KEY) : null;
    const n = normalize(saved);
    if (n) setUserState(n);
    setReady(true);
  }, [seed]);

  const setUser = (u: CurrentUser) => {
    setUserState(u);
    try {
      localStorage.setItem(KEY, u);
    } catch {}
  };

  return (
    <UserCtx.Provider value={{ user, setUser, ready }}>
      {children}
      {ready && !user && <UserPicker onPick={setUser} />}
    </UserCtx.Provider>
  );
}

function UserPicker({ onPick }: { onPick: (u: CurrentUser) => void }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-3xl bg-[var(--surface)] p-6 shadow-2xl">
        <div className="text-center">
          <div className="text-xl font-bold">누구세요?</div>
          <div className="mt-1 text-sm text-[var(--muted)]">이 기기에서 앞으로 기본 설정됩니다</div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          {([
            { v: "suhyeon" as const, label: "수현", emoji: "🧑", color: "#3b82f6" },
            { v: "wife" as const, label: "수진", emoji: "👰", color: "#ec4899" },
          ]).map((u) => (
            <button
              key={u.v}
              onClick={() => onPick(u.v)}
              className={cn(
                "flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl transition active:scale-95",
              )}
              style={{ backgroundColor: `${u.color}22`, color: u.color }}
            >
              <span className="text-4xl">{u.emoji}</span>
              <span className="text-base font-semibold">{u.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 text-center text-[11px] text-[var(--muted)]">
          나중에 설정에서 변경 가능
        </div>
      </div>
    </div>
  );
}
